import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardCheck,
  Plus,
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  Loader2,
  Save,
  X,
  GripVertical,
  ArrowUp,
  ArrowDown,
  DoorOpen,
  DoorClosed,
  CalendarClock,
  ShieldCheck,
  Sparkles,
  Sun,
  Tag,
  Calculator,
  Package,
  CheckCircle2,
  Circle,
} from 'lucide-react';

const ICON_MAP = {
  'door-open': DoorOpen,
  'door-closed': DoorClosed,
  'calendar-clock': CalendarClock,
  'shield-check': ShieldCheck,
  'sparkles': Sparkles,
  'sun': Sun,
  'tag': Tag,
  'calculator': Calculator,
  'package': Package,
  'clipboard': ClipboardCheck,
};

const ICON_COLORS = [
  'bg-blue-500/10 text-blue-600',
  'bg-indigo-500/10 text-indigo-600',
  'bg-amber-500/10 text-amber-600',
  'bg-emerald-500/10 text-emerald-600',
  'bg-cyan-500/10 text-cyan-600',
  'bg-orange-500/10 text-orange-600',
  'bg-pink-500/10 text-pink-600',
  'bg-violet-500/10 text-violet-600',
  'bg-teal-500/10 text-teal-600',
];

export function ProceduresPage() {
  const { api } = useAuth();
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editingProc, setEditingProc] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [formTitle, setFormTitle] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formItems, setFormItems] = useState([{ text: '', done: false }]);

  const fetchProcedures = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/procedures/');
      setProcedures(res.data);
    } catch (err) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchProcedures(); }, [fetchProcedures]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/procedures/seed');
      toast.success(res.data.message);
      fetchProcedures();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSeeding(false);
    }
  };

  const openNew = () => {
    setEditingProc(null);
    setFormTitle('');
    setFormNotes('');
    setFormItems([{ text: '', done: false }]);
    setShowEditor(true);
  };

  const openEdit = (proc) => {
    setEditingProc(proc);
    setFormTitle(proc.title || '');
    setFormNotes(proc.notes || '');
    setFormItems(proc.items?.length ? proc.items.map(i => ({ ...i })) : [{ text: '', done: false }]);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formTitle.trim()) { toast.error("Titre requis"); return; }
    setSaving(true);
    try {
      const data = {
        title: formTitle,
        notes: formNotes,
        items: formItems.filter(i => i.text.trim()),
      };
      if (editingProc) {
        await api.put(`/procedures/${editingProc.id}`, data);
        toast.success("Procédure mise à jour");
      } else {
        await api.post('/procedures/', data);
        toast.success("Procédure créée");
      }
      setShowEditor(false);
      fetchProcedures();
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (procId) => {
    if (!window.confirm("Supprimer cette procédure ?")) return;
    try {
      await api.delete(`/procedures/${procId}`);
      toast.success("Procédure supprimée");
      fetchProcedures();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  // Inline quick-edit: toggle item done or edit text
  const updateItemInline = async (proc, itemIndex, newText) => {
    const newItems = proc.items.map((item, i) =>
      i === itemIndex ? { ...item, text: newText } : item
    );
    try {
      await api.put(`/procedures/${proc.id}`, { items: newItems });
      fetchProcedures();
    } catch (err) {
      toast.error("Erreur de mise à jour");
    }
  };

  const addItemInline = async (proc) => {
    const newItems = [...(proc.items || []), { text: "Nouvel élément", done: false }];
    try {
      await api.put(`/procedures/${proc.id}`, { items: newItems });
      fetchProcedures();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const removeItemInline = async (proc, itemIndex) => {
    const newItems = proc.items.filter((_, i) => i !== itemIndex);
    try {
      await api.put(`/procedures/${proc.id}`, { items: newItems });
      fetchProcedures();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const moveItemInline = async (proc, fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= proc.items.length) return;
    const newItems = [...proc.items];
    [newItems[fromIndex], newItems[toIndex]] = [newItems[toIndex], newItems[fromIndex]];
    try {
      await api.put(`/procedures/${proc.id}`, { items: newItems });
      fetchProcedures();
    } catch (err) { toast.error("Erreur"); }
  };

  const addFormItem = () => setFormItems(p => [...p, { text: '', done: false }]);
  const removeFormItem = (i) => setFormItems(p => p.filter((_, idx) => idx !== i));
  const updateFormItem = (i, text) => setFormItems(p => {
    const n = [...p]; n[i] = { ...n[i], text }; return n;
  });
  const moveFormItem = (i, direction) => {
    const j = i + direction;
    if (j < 0 || j >= formItems.length) return;
    setFormItems(p => { const n = [...p]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  };

  return (
    <div className="space-y-6" data-testid="procedures-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <ClipboardCheck className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-heading font-bold">Procédures du restaurant</h2>
            <p className="text-sm text-muted-foreground">{procedures.length} procédures</p>
          </div>
        </div>
        <div className="flex gap-2">
          {procedures.length === 0 && (
            <Button variant="outline" onClick={handleSeed} disabled={seeding} data-testid="seed-procedures-btn">
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Charger les procédures
            </Button>
          )}
          <Button onClick={openNew} data-testid="add-procedure-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle procédure
          </Button>
        </div>
      </div>

      {/* Procedures list */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : procedures.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ClipboardCheck className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-lg">Aucune procédure</p>
            <p className="text-sm text-muted-foreground mt-1">Cliquez sur "Charger les procédures" pour commencer</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {procedures.map((proc, pIdx) => {
            const isExp = expandedId === proc.id;
            const IconComp = ICON_MAP[proc.icon] || ClipboardCheck;
            const colorClass = ICON_COLORS[pIdx % ICON_COLORS.length];
            const itemCount = proc.items?.length || 0;

            return (
              <Card key={proc.id} className="overflow-hidden" data-testid={`proc-card-${proc.id}`}>
                {/* Header */}
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedId(isExp ? null : proc.id)}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                    <IconComp className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold">{proc.title}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="secondary" className="text-xs">{itemCount} éléments</Badge>
                      {proc.notes && <span className="text-xs text-muted-foreground truncate max-w-[300px]">{proc.notes}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      onClick={e => { e.stopPropagation(); openEdit(proc); }}
                      data-testid={`edit-proc-${proc.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                      onClick={e => { e.stopPropagation(); handleDelete(proc.id); }}
                      data-testid={`delete-proc-${proc.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {isExp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>

                {/* Expanded content - editable checklist */}
                {isExp && (
                  <div className="border-t border-border/50 px-4 pb-4 pt-3 space-y-2">
                    {proc.notes && (
                      <p className="text-sm text-muted-foreground italic mb-3 px-2">{proc.notes}</p>
                    )}

                    <Button variant="outline" size="sm" className="mb-2 border-dashed"
                      onClick={() => addItemInline(proc)} data-testid={`add-item-top-${proc.id}`}>
                      <Plus className="w-3 h-3 mr-1" /> Ajouter un élément
                    </Button>

                    {proc.items?.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 group" data-testid={`proc-item-${proc.id}-${i}`}>
                        <div className="flex flex-col shrink-0">
                          <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => moveItemInline(proc, i, -1)} disabled={i === 0}
                            data-testid={`move-up-${proc.id}-${i}`}>
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => moveItemInline(proc, i, 1)} disabled={i === proc.items.length - 1}
                            data-testid={`move-down-${proc.id}-${i}`}>
                            <ArrowDown className="w-3 h-3" />
                          </Button>
                        </div>
                        <Circle className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                        <InlineEditItem
                          text={item.text}
                          onSave={(newText) => updateItemInline(proc, i, newText)}
                        />
                        <Button variant="ghost" size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive shrink-0"
                          onClick={() => removeItemInline(proc, i)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle data-testid="proc-editor-title">
              {editingProc ? 'Modifier la procédure' : 'Nouvelle procédure'}
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-4 pb-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Titre</label>
                <Input value={formTitle} onChange={e => setFormTitle(e.target.value)}
                  placeholder="Ex: Procédure d'ouverture" data-testid="proc-title-input" />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Notes</label>
                <Textarea value={formNotes} onChange={e => setFormNotes(e.target.value)}
                  placeholder="Instructions ou notes supplémentaires..."
                  rows={2} data-testid="proc-notes-input" />
              </div>

              <Separator />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Éléments de la checklist</label>
                  <Button variant="outline" size="sm" onClick={addFormItem} data-testid="add-form-item-btn">
                    <Plus className="w-3 h-3 mr-1" /> Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {formItems.map((item, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <div className="flex flex-col shrink-0">
                        <Button variant="ghost" size="icon" className="h-5 w-5"
                          onClick={() => moveFormItem(i, -1)} disabled={i === 0}
                          data-testid={`form-move-up-${i}`}>
                          <ArrowUp className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-5 w-5"
                          onClick={() => moveFormItem(i, 1)} disabled={i === formItems.length - 1}
                          data-testid={`form-move-down-${i}`}>
                          <ArrowDown className="w-3 h-3" />
                        </Button>
                      </div>
                      <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                      <Input placeholder={`Élément ${i + 1}`} value={item.text}
                        onChange={e => updateFormItem(i, e.target.value)}
                        data-testid={`form-item-${i}`} />
                      {formItems.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive"
                          onClick={() => removeFormItem(i)}>
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowEditor(false)} data-testid="cancel-proc-btn">Annuler</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-proc-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingProc ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===== Inline editable text ===== */
function InlineEditItem({ text, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text);

  const save = () => {
    if (value.trim() && value !== text) {
      onSave(value.trim());
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <Input
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        autoFocus
        className="flex-1 h-8 text-sm"
      />
    );
  }

  return (
    <span
      className="flex-1 text-sm cursor-pointer hover:bg-muted/50 px-2 py-1 rounded transition-colors"
      onClick={() => { setValue(text); setEditing(true); }}
      data-testid="inline-edit-text"
    >
      {text}
    </span>
  );
}
